export type MetricContent = {
  title: string
  body: string
}

export type PartnerContent = {
  label: string
  title: string
  body: string
}

export type PageContent = {
  eyebrow: string
  title: string
  body: string
  formEyebrow: string
  formTitle: string
}

export type SiteContent = {
  nav: {
    brand: string
    shelfOwnersCta: string
    brandsCta: string
  }
  hero: {
    eyebrow: string
    title: string
    body: string
    noteEyebrow: string
    noteTitle: string
  }
  media: {
    heroImageUrl: string
    shelfOwnerImageUrl: string
    brandImageUrl: string
  }
  metrics: MetricContent[]
  partners: {
    eyebrow: string
    title: string
    realEstate: PartnerContent
    brands: PartnerContent
  }
  footer: {
    eyebrow: string
    title: string
    body: string
    contactEyebrow: string
    email: string
  }
  realEstatePage: PageContent
  brandsPage: PageContent
}

export const defaultSiteContent: SiteContent = {
  nav: {
    brand: 'Shelf Discovery',
    shelfOwnersCta: 'For Shelf Owners',
    brandsCta: 'For Brands',
  },
  hero: {
    eyebrow: 'Retail media for trusted physical spaces',
    title: 'Where premium places become discovery channels.',
    body:
      'Shelf Discovery connects real estate owners and consumer brands through curated offline placements designed for visibility, footfall, and measurable neighborhood demand.',
    noteEyebrow: 'Curated placements',
    noteTitle: 'Shelves, lobbies, studios, and community retail.',
  },
  media: {
    heroImageUrl: '',
    shelfOwnerImageUrl: '',
    brandImageUrl: '',
  },
  metrics: [
    {
      title: 'Retail',
      body: 'High-context product discovery',
    },
    {
      title: 'Real estate',
      body: 'Footfall converted into media value',
    },
    {
      title: 'Brands',
      body: 'Premium offline visibility',
    },
  ],
  partners: {
    eyebrow: 'Partner paths',
    title: 'Built for both sides of the shelf.',
    realEstate: {
      label: 'For real estate owners',
      title: 'Monetize high-intent footfall',
      body: 'Turn lobby, retail, and community traffic into a curated media channel.',
    },
    brands: {
      label: 'For brands',
      title: 'Seek premium offline visibility',
      body: 'Launch inside trusted physical spaces where discovery already happens.',
    },
  },
  footer: {
    eyebrow: 'About us',
    title: 'Shelf Discovery helps physical spaces become discovery-led retail media channels.',
    body:
      'A platform that unites shelf owners like cafe, retail marts, restaurants etc and consumer brands through thoughtfully curated offline placements in trusted spaces, enabling impactful visibility, smarter utilization of footfall, and sustained brand partnerships.',
    contactEyebrow: 'Contact',
    email: 'support@shelfdiscovery.in',
  },
  realEstatePage: {
    eyebrow: 'For Shelf Owners',
    title: 'Monetize high-intent footfall with curated shelf media.',
    body:
      'Turn lobby, retail, and community traffic into a curated media channel. Shelf Discovery helps property teams and shelf owners create brand partnerships that add value for visitors while unlocking a new revenue layer.',
    formEyebrow: 'Start a conversation',
    formTitle: 'Tell us about your space.',
  },
  brandsPage: {
    eyebrow: 'For Brands',
    title: 'Premium offline visibility for consumer brands.',
    body:
      'Launch inside trusted physical spaces where discovery already happens. Shelf Discovery helps brands test curated shelves, community spaces, and real-world touchpoints with a focused path from visibility to measurable local demand.',
    formEyebrow: 'Brand enquiry',
    formTitle: 'Tell us about your brand.',
  },
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const mergeDeep = <T>(base: T, override: unknown): T => {
  if (Array.isArray(base)) {
    return (Array.isArray(override) ? override : base) as T
  }

  if (!isRecord(base)) {
    return (override ?? base) as T
  }

  const output: Record<string, unknown> = { ...base }

  if (!isRecord(override)) {
    return output as T
  }

  for (const [key, value] of Object.entries(override)) {
    output[key] = mergeDeep(output[key], value)
  }

  return output as T
}

export const mergeSiteContent = (content?: unknown): SiteContent => {
  return mergeDeep(defaultSiteContent, content)
}
